import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { messaggioErrore } from '../../utils/agenda';
import Icona from '../cliente/Icone';

// Impostazioni → Estrattore → **Portali** (backend/portali.py).
// I tre portali non reggono lo stesso numero di pagine: immobiliare.it ne regge molte, idealista si arrabbia
// molto prima, casa.it sta nel mezzo. Qui ognuno ha il suo limite al giorno (di base 120 / 40 / 80) e il suo
// bottone «pausa 24 ore» per fermarlo a mano quando si vede che è nervoso.
// La pausa che si mette da sola — «accesso temporaneamente limitato» letto dall'estensione — si vede qui come
// le altre, con scritto perché.

export const quando = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const oggi = new Date();
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === oggi.toDateString()) return `oggi alle ${ora}`;
  const domani = new Date(oggi.getTime() + 86400000);
  if (d.toDateString() === domani.toDateString()) return `domani alle ${ora}`;
  return `${d.getDate()} ${d.toLocaleDateString('it-IT', { month: 'short' })} alle ${ora}`;
};

// La riga sotto il nome: perché è fermo, o quanto gli resta oggi
export const riga = (p) => {
  if (p.in_pausa) {
    const perche = p.pausa_motivo === 'accesso limitato' ? 'accesso limitato' : 'in pausa';
    return `${perche} fino a ${quando(p.pausa_fino_a)}`;
  }
  if (p.limite === 0) return 'spento: non si apre';
  if (p.fermo) return `finite le ${p.limite} pagine di oggi`;
  return `${p.oggi} aperte oggi · ne restano ${p.restano}`;
};

const PortaliEstrattore = () => {
  const [dati, setDati] = useState(null);
  const [modulo, setModulo] = useState(null);
  const [attesa, setAttesa] = useState('');

  const prendi = useCallback((d) => {
    setDati(d);
    setModulo(Object.fromEntries(d.portali.map((p) => [p.portale, String(p.limite)])));
  }, []);

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/admin/estrattore/portali`).then((r) => vivo && prendi(r.data)).catch(() => vivo && setDati(false));
    return () => { vivo = false; };
  }, [prendi]);

  if (dati === false) return <section className="card"><p className="vuoto">Non riesco a leggere i limiti dei portali.</p></section>;
  if (!dati) return null;

  const salva = async () => {
    setAttesa('salva');
    try {
      const limiti = Object.fromEntries(Object.entries(modulo).map(([k, v]) => [k, Number(v) || 0]));
      const r = await axios.put(`${API}/admin/estrattore/portali`, { limiti });
      prendi(r.data);
      toast.success('Limiti salvati');
    } catch (e) { toast.error(messaggioErrore(e, 'Limiti non salvati')); } finally { setAttesa(''); }
  };

  const pausa = async (portale, metti) => {
    setAttesa(portale);
    try {
      const r = metti
        ? await axios.post(`${API}/admin/estrattore/portali/${portale}/pausa`)
        : await axios.delete(`${API}/admin/estrattore/portali/${portale}/pausa`);
      prendi(r.data);
      toast.success(metti ? `In pausa fino a ${quando(r.data.portali.find((p) => p.portale === portale)?.pausa_fino_a)}` : 'Riprende');
    } catch (e) { toast.error(messaggioErrore(e, 'Non riuscito')); } finally { setAttesa(''); }
  };

  const cambiato = dati.portali.some((p) => String(p.limite) !== modulo[p.portale]);

  return (
    <section className="card" data-testid="portali-estrattore">
      <div className="ttl"><div className="i b"><Icona nome="impostazioni" /></div><h2>Portali <small>pagine al giorno</small></h2></div>
      <p className="imp-nota">
        Quante pagine al giorno si possono aprire su ogni portale, e il bottone per fermarne uno per 24 ore.
        I portali non sono uguali: idealista si arrabbia molto prima degli altri. Un portale fermo non riceve
        nessun lavoro — né annunci, né ricerche, né ricontrolli.
      </p>
      <div className="imp-portali">
        {dati.portali.map((p) => (
          <div key={p.portale} className={`portale${p.fermo ? ' fermo' : ''}`} data-testid={`portale-${p.portale}`}>
            <div style={{ minWidth: 0 }}>
              <b>{p.nome}</b>
              <span className={p.fermo ? 'w' : 'muto'} data-testid={`portale-riga-${p.portale}`}>{riga(p)}</span>
            </div>
            <label>
              al giorno
              <input type="number" min="0" max={dati.massimo} value={modulo[p.portale] ?? ''}
                aria-label={`Pagine al giorno su ${p.nome}`}
                onChange={(e) => setModulo({ ...modulo, [p.portale]: e.target.value })} />
            </label>
            <button type="button" className="btn sm" disabled={attesa === p.portale}
              data-testid={`pausa-${p.portale}`} onClick={() => pausa(p.portale, !p.in_pausa)}>
              {p.in_pausa ? 'Riprendi' : `Pausa ${dati.ore_pausa} ore`}
            </button>
          </div>
        ))}
      </div>
      <div className="imp-azioni">
        <button type="button" className="btn sm p" disabled={!cambiato || attesa === 'salva'} onClick={salva}>
          {attesa === 'salva' ? 'Salvo…' : 'Salva i limiti'}
        </button>
        <button type="button" className="link" onClick={() => setModulo(Object.fromEntries(
          Object.entries(dati.predefiniti).map(([k, v]) => [k, String(v)])))}>Valori di base</button>
      </div>
    </section>
  );
};

export default PortaliEstrattore;
